import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PUBLIC_URL, AUTH_URL, GoogleOAuthClientID } from "./../../const";
import { useAuth } from "./../../AuthContext";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import LanguageSwitcher from "../core/switch-language";

// --- i18n: simple dictionary ---
const translations = {
    en: {
        welcomeTitle: "Welcome",
        welcomeTo: "to My-Sites-Guard.com",
        clientArea: "Client Area",
        orSignInWith: "or sign in with",
        email: "Email",
        password: "Password",
        rememberDevice: "Remember this Device",
        forgotPassword: "Forgot Password?",
        signIn: "Sign In",
        signingIn: "Signing in...",
        noAccount: "Don't have an account?",
        createAccount: "Create an account",
    },
    ru: {
        welcomeTitle: "Добро пожаловать",
        welcomeTo: "на My-Sites-Guard.com",
        clientArea: "Личный кабинет",
        orSignInWith: "или войдите через",
        email: "Email",
        password: "Пароль",
        rememberDevice: "Запомнить это устройство",
        forgotPassword: "Забыли пароль?",
        signIn: "Войти",
        signingIn: "Входим...",
        noAccount: "Нет аккаунта?",
        createAccount: "Создать аккаунт",
    },
    ua: {
        welcomeTitle: "Ласкаво просимо",
        welcomeTo: "до My-Sites-Guard.com",
        clientArea: "Кабінет клієнта",
        orSignInWith: "або увійдіть через",
        email: "Email",
        password: "Пароль",
        rememberDevice: "Запам'ятати цей пристрій",
        forgotPassword: "Забули пароль?",
        signIn: "Увійти",
        signingIn: "Вхід...",
        noAccount: "Немає акаунта?",
        createAccount: "Створити акаунт",
    },
};

const getInitialLang = () =>
    (localStorage.getItem("lang") || "").toLowerCase() || "en";

const LoginButton = ({ setError, lang }) => {
    const navigate = useNavigate();
    const { loginGoogle } = useAuth();

    const handleSuccess = async (response) => {
        try {
            const token = response.credential;
            const data = await loginGoogle(token, "1", lang);

            if (data.status === 200) {
                navigate(data.forward);
            } else {
                setError && setError(data.error);
            }
        } catch (e) {
            setError && setError("Google login error");
        }
    };

    const handleError = () => {
        setError && setError("Google login failed");
    };

    return <GoogleLogin onSuccess={handleSuccess} onError={handleError} />;
};

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    // --- language state ---
    const [lang, setLang] = useState(getInitialLang());
    const t = useMemo(() => translations[lang] || translations.en, [lang]);

    useEffect(() => {
        localStorage.setItem("lang", lang);
        document.documentElement.lang = lang;
    }, [lang]);

    // --- auth state ---
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberDevice, setRememberDevice] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const data = await login(email, password, "1", rememberDevice ? "1" : "0", lang);
            if (data.status === 200) {
                navigate(data.forward);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div id="main-wrapper">
                <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100">
                    <div className="position-relative z-index-5">
                        <div className="row">
                            <div className="col-xl-7 col-xxl-8">
                                <a href="https://my-sites-guard.com/" className="text-nowrap logo-img d-block px-4 py-9 w-100">
                                    <img
                                        src={`https://my-sites-guard.com/assets/img/home_logo.png`}
                                        className="dark-logo"
                                        alt="Logo"
                                        style={{ maxHeight: "40px" }}
                                    />
                                </a>
                                <div className="d-none d-xl-flex align-items-center justify-content-center h-n80">
                                    <img
                                        src={`${PUBLIC_URL}/assets/images/backgrounds/login-bg.png`}
                                        alt=""
                                        className="img-fluid"
                                        width="500"
                                    />
                                </div>
                            </div>

                            <div className="col-xl-5 col-xxl-4">
                                <div className="authentication-login min-vh-100 bg-body row justify-content-center align-items-center p-4">
                                    <div className="auth-max-width col-sm-8 col-md-6 col-xl-7 px-4">
                                        {/* header + language switcher */}
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h2 className="mb-1 fs-7 fw-bolder">
                                                    {t.welcomeTitle}
                                                    <br />
                                                    {t.welcomeTo}
                                                </h2>
                                                <p className="mb-0">{t.clientArea}</p>
                                            </div>
                                        </div>

                                        <LanguageSwitcher lang={lang} setLang={setLang} />

                                        {/* Google */}
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <GoogleOAuthProvider clientId={GoogleOAuthClientID}>
                                                    <LoginButton setError={setError} />
                                                </GoogleOAuthProvider>
                                            </div>
                                        </div>

                                        {/* separator */}
                                        <div className="position-relative text-center my-4">
                                            <p className="mb-0 fs-4 px-3 d-inline-block bg-body text-dark z-index-5 position-relative">
                                                {t.orSignInWith}
                                            </p>
                                            <span className="border-top w-100 position-absolute top-50 start-50 translate-middle"></span>
                                        </div>

                                        {error && <p className="alert alert-danger text-danger">{error}</p>}

                                        {/* form */}
                                        <form onSubmit={handleLogin}>
                                            <div className="mb-3">
                                                <label htmlFor="email" className="form-label">
                                                    {t.email}
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div className="mb-4">
                                                <label htmlFor="password" className="form-label">
                                                    {t.password}
                                                </label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div className="d-flex align-items-center justify-content-between mb-4">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input primary"
                                                        type="checkbox"
                                                        id="flexCheckChecked"
                                                        checked={rememberDevice}
                                                        onChange={(e) => setRememberDevice(e.target.checked)}
                                                    />
                                                    <label className="form-check-label text-dark fs-3" htmlFor="flexCheckChecked">
                                                        {t.rememberDevice}
                                                    </label>
                                                </div>
                                                <a className="text-primary fw-medium fs-3" href="/forgot-password/">
                                                    {t.forgotPassword}
                                                </a>
                                            </div>

                                            <button type="submit" className="btn btn-primary w-100 py-8 mb-4 rounded-2" disabled={loading}>
                                                {loading ? (
                                                    <span>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        {t.signingIn}
                          </span>
                                                ) : (
                                                    t.signIn
                                                )}
                                            </button>

                                            <div className="d-flex align-items-center justify-content-center">
                                                <p className="fs-4 mb-0 fw-medium">{t.noAccount}</p>
                                                <a className="text-primary fw-medium ms-2" href="/sign-up/">
                                                    {t.createAccount}
                                                </a>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            {/* /right column */}
                        </div>
                    </div>
                </div>
            </div>

            <div className="dark-transparent sidebartoggler"></div>

            {/* vendor scripts as у тебя */}
            <script src="./assets/libs/bootstrap/dist/js/bootstrap.bundle.min.js"></script>
            <script src="./assets/libs/simplebar/dist/simplebar.min.js"></script>
            <script src="./assets/js/theme/app.init.js"></script>
            <script src="./assets/js/theme/theme.js"></script>
            <script src="./assets/js/theme/app.min.js"></script>
            <script src="./assets/js/theme/sidebarmenu.js"></script>
            <script src="https://cdn.jsdelivr.net/npm/iconify-icon@1.0.8/dist/iconify-icon.min.js"></script>
        </>
    );
};

export default Login;
